import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Psychology,
  AutoAwesome,
  Payments,
  Lightbulb,
  Analytics,
} from "@mui/icons-material";

const MotionPaper = motion(Paper);

const aiFeatures = [
  {
    icon: <Psychology sx={{ fontSize: 48 }} />,
    title: "Insights Estratégicos",
    description:
      "Receba análises inteligentes e recomendações acionáveis geradas automaticamente a partir dos seus dados de SWOT, metas e planos de ação.",
    color: "#7C3AED",
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 48 }} />,
    title: "Geração de Metas",
    description:
      "Crie metas SMART automaticamente baseadas no contexto da sua empresa, com indicadores e resultados alinhados à sua estratégia.",
    color: "#EF443C",
  },
  {
    icon: <Payments sx={{ fontSize: 48 }} />,
    title: "PLR Inteligente",
    description:
      "Estruture regras de participação nos lucros justas e motivacionais, vinculadas automaticamente às metas de performance.",
    color: "#10B981",
  },
  {
    icon: <Lightbulb sx={{ fontSize: 48 }} />,
    title: "Missão, Visão e Valores",
    description:
      "Gere opções profissionais de declarações de missão, visão e valores para sua empresa com base em questionário guiado.",
    color: "#F59E0B",
  },
  {
    icon: <Analytics sx={{ fontSize: 48 }} />,
    title: "Análise Inteligente",
    description:
      "Identifique oportunidades, ameaças e pontos de melhoria automaticamente através de análise preditiva dos seus dados.",
    color: "#3B82F6",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const AIFeaturesSection: React.FC = () => {
  return (
    <Box
      id="ai-features"
      sx={{
        py: { xs: 10, md: 14 },
        backgroundColor: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "120%",
          height: "120%",
          background:
            "radial-gradient(ellipse at center, rgba(239,68,60,0.03) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Chip
              label="Powered by Google Gemini AI"
              sx={{
                mb: 3,
                px: 2,
                py: 2.5,
                fontSize: "0.9rem",
                fontWeight: 600,
                backgroundColor: "#042E4B",
                color: "white",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
              icon={
                <AutoAwesome
                  sx={{
                    color: "#F59E0B !important",
                    fontSize: 20,
                  }}
                />
              }
            />
          </motion.div> */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#042E4B",
                mb: 2,
                fontSize: { xs: "2rem", md: "2.75rem" },
              }}
            >
              Potencializado por{" "}
              <Box component="span" sx={{ color: "#EF443C" }}>
                Inteligência Artificial
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#64748B",
                fontWeight: 400,
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              Nossa plataforma utiliza inteligência artificial avançada para
              automatizar análises complexas e gerar recomendações estratégicas
              personalizadas para sua empresa.
            </Typography>
          </motion.div>
        </Box>

        {/* AI Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <Grid container spacing={4}>
            {aiFeatures.map((feature) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.title}>
                <MotionPaper
                  variants={itemVariants}
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid #E2E8F0",
                    transition: "all 0.3s ease",
                    cursor: "default",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                      borderColor: feature.color,
                    },
                  }}
                >
                  <Stack spacing={2.5}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        backgroundColor: `${feature.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#042E4B",
                      }}
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748B",
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Stack>
                </MotionPaper>
              </Grid>
            ))}

            {/* Extra Card - CTA */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <MotionPaper
                variants={itemVariants}
                elevation={0}
                sx={{
                  p: 4,
                  height: "100%",
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #042E4B 0%, #0a4a6e 100%)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "white",
                    mb: 2,
                  }}
                >
                  Experimente o poder da IA
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    mb: 3,
                  }}
                >
                  Comece agora e veja como a inteligência artificial pode
                  transformar seu planejamento estratégico.
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#EF443C",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => {
                    globalThis.location.href = "/tw/login?tab=cadastro";
                  }}
                >
                  Começar gratuitamente →
                </Box>
              </MotionPaper>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AIFeaturesSection;
